/**
 * VerifyScreen Component
 * ONE-TO-ONE CLONE of AuthScreen gesture capture logic
 * No challenge matching - just capture gestures like auth does
 */

// Space Mission assets
import bluePlanetSrc   from '../assets/blue-planet-prod.png';
import greenPlanetSrc  from '../assets/green-planet-prod.png';
import purplePlanetSrc from '../assets/purple-planet-prod.png';
import redPlanetSrc    from '../assets/red-planet-prod.png';
import tanPlanetSrc    from '../assets/tan-planet-prod.png';
import shipSrc         from '../assets/ship-prod.png';

const SPACE_PLANET_COLORS = ['blue', 'green', 'purple', 'red', 'tan'];
const SPACE_PLANET_CSS = {
    blue:   '#4A9EFF',
    green:  '#4ADE80',
    purple: '#A78BFA',
    red:    '#F87171',
    tan:    '#D9A96A',
};

export class VerifyScreen {
    constructor(options) {
        this.container = options.container;
        this.apiUrl = options.apiUrl || 'ws://localhost:8001';
        this.applicationId = options.applicationId;
        this.onSuccess = options.onSuccess;
        this.onError = options.onError;

        // State - EXACT same as AuthScreen
        this.webcamStream = null;
        this.websocket = null;
        this.isCapturing = false;
        this.frameInterval = null;
        this.gesturesRecorded = 0;
        this.noHandsFrameCount = 0;
        this.hasStartedGestures = false;
        this.FRAME_RATE = 10;
        this.NO_HANDS_THRESHOLD = 20;
        
        // Challenge system state
        this.challenges = [];
        this.currentStep = 0;

        // Verify mode (fetched before render)
        this.verifyMode = 'gesture'; // default until fetched

        // Touch mode state
        this.isTouchMode = false;
        this.touchBubbles = [];
        this.currentTargetBubble = 0;
        this.contactFrameCount = 0;
        this.CONTACT_FRAMES_REQUIRED = 2;

        // Space Mission state
        this.isSpaceMissionMode = false;
        this.spacePlanets = [];
        this.spaceStars = [];
        this.currentTargetPlanet = 0;
        this._spaceImagesCache = null;
        this._shipX = null;       // smoothed render position
        this._shipY = null;
        this._shipTargetX = null; // latest server-reported position
        this._shipTargetY = null;
        this._spaceRafId = null;  // rAF handle for the render loop

        // Preloaded from /api/app-mode (available before WebSocket challenge)
        this.preloadedConfigurations = {};
        
        // Configurations from server (received with challenge)
        this.configurations = {};

        // Pointer tracking state
        this._pointerEvents = [];
        this._pointerHandler = null;
        this._pointerScore = null;
        this._headlessSuspect = false;

        // Mobile touch-drag mode (replaces hand tracking on mobile/tablet Touch apps)
        this._isMobileDevice = this.detectMobileDevice();
        this._mobileDragMode = false;
        this._dragActive = false;
        this._dragPointerId = null;
        this._mobileDragRafId = null;
        this._mobileDragListenersAttached = false;
        this._mobileDragEvents = [];

        // DOM elements
        this.elements = {};
    }

    /**
     * Start capture flow
     */
    async start() {
        await this.fetchVerifyMode();
        this._mobileDragMode = this._isMobileDevice
            && this.verifyMode === 'touch'
            && this.preloadedConfigurations?.mobile_mode === true;
        this.render();
        this.setupEventListeners();
        return new Promise((resolve, reject) => {
            this.resolvePromise = resolve;
            this.rejectPromise = reject;
        });
    }

    /**
     * Fetch the verify mode (gesture/touch) from the API before rendering
     */
    async fetchVerifyMode() {
        try {
            const httpUrl = this.apiUrl.replace(/^wss:\/\//, 'https://').replace(/^ws:\/\//, 'http://');
            const resp = await fetch(`${httpUrl}/api/app-mode?application_id=${encodeURIComponent(this.applicationId)}`);
            if (resp.ok) {
                const data = await resp.json();
                this.verifyMode = data.mode || 'gesture';
                this.preloadedConfigurations = data.configurations || {};
            }
        } catch (e) {
            this.verifyMode = 'gesture'; // fallback
        }
    }

    /**
     * Render the UI - EXACT copy of AuthScreen structure
     */
    render() {
        // Match EXACT AuthScreen HTML structure
        this.container.innerHTML = `
            <!-- Main Content -->
            <div class="auth-content">
                <div class="auth-prompt" style="margin-bottom: 12px;">
                    <h1>Verify You're Human</h1>
                    <ol id="instructionList" style="text-align: left; max-width: 500px; margin: 0 auto; padding-left: 20px; font-size: 15px; color: #6B7280; line-height: 1.8;">
                        ${this._mobileDragMode && this.preloadedConfigurations?.space_mission_theme ? `
                        <li>Touch and hold anywhere on the screen</li>
                        <li>Colored planets will appear on screen</li>
                        <li>Drag your finger continuously to visit each planet in order</li>
                        <li>Do not lift your finger until you're done</li>
                        ` : this._mobileDragMode ? `
                        <li>Touch and hold anywhere on the screen</li>
                        <li>Numbered bubbles will appear on screen</li>
                        <li>Drag your finger continuously to pop each bubble in order</li>
                        <li>Do not lift your finger until you're done</li>
                        ` : this.verifyMode === 'touch' && this.preloadedConfigurations?.space_mission_theme ? `
                        <li>Face the camera and position your hand in view</li>
                        <li>Colored planets will appear on screen</li>
                        <li>Guide the spaceship to each planet in order</li>
                        <li>Complete all planets to verify</li>
                        ` : this.verifyMode === 'touch' ? `
                        <li>Face the camera and position your hand in view</li>
                        <li>Numbered bubbles will appear on screen</li>
                        <li>Touch each bubble in order to pop it</li>
                        <li>Complete all bubbles to verify</li>
                        ` : `
                        <li>Face the camera and position your hand in view</li>
                        <li>Match the gesture sequence described</li>
                        <li>Hold each gesture steady until it's recognized</li>
                        <li>Complete all gestures to verify</li>
                        `}
                    </ol>
                </div>

                <!-- Challenge Prompt - hidden until verify starts -->
                <div id="challengePrompt" class="verification-progress" style="display: none; text-align: center; margin: 8px auto; max-width: 500px;">
                    <div id="challengeText" class="progress-label" style="font-weight: 600; color: #0A2540; margin-bottom: 10px;">Loading challenge...</div>
                    <div id="progressDots" class="progress-dots" style="display: flex; justify-content: center; gap: 12px; align-items: center;">
                        <div class="progress-dot" id="dot0" style="width: 16px; height: 16px; border-radius: 50%; background: #E5E7EB; border: 2px solid #D1D5DB; transition: all 0.3s ease;"></div>
                        <div class="progress-dot" id="dot1" style="width: 16px; height: 16px; border-radius: 50%; background: #E5E7EB; border: 2px solid #D1D5DB; transition: all 0.3s ease;"></div>
                        <div class="progress-dot" id="dot2" style="width: 16px; height: 16px; border-radius: 50%; background: #E5E7EB; border: 2px solid #D1D5DB; transition: all 0.3s ease;"></div>
                    </div>
                </div>

                <!-- Video Feed - hidden until verify starts -->
                <div class="video-wrapper" style="display: none; margin-bottom: 12px;">
                    <video id="webcam" autoplay playsinline></video>
                    <canvas id="overlay" width="640" height="480"></canvas>
                    
                    <!-- Gesture Counter Overlay - EXACT COPY from AuthScreen -->
                    <div id="gestureInfo" class="gesture-info" style="position: absolute; top: 12px; right: 12px; background: rgba(249, 250, 251, 0.95); padding: 8px 16px; border-radius: 6px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); z-index: 10;">
                        <div class="gesture-count" style="gap: 6px;">
                            <span id="gestureCount" style="font-size: 24px; font-weight: 600; color: #0A2540;">0</span>
                            <span class="gesture-label" style="font-size: 13px; color: #6B7280;">gestures</span>
                        </div>
                    </div>
                    
                    <!-- Loading Overlay -->
                    <div id="loadingOverlay" class="loading-overlay" style="display: none;">
                        <div class="loading-spinner"></div>
                        <p>Verifying...</p>
                    </div>
                </div>

                <!-- Status Messages - hidden until verify starts -->
                <div id="statusMessage" class="status-message" style="display: none;">
                    <div id="connectionStatus" style="display: none;"></div>
                    <div id="authStatus"></div>
                </div>

                <!-- Controls -->
                <div class="auth-controls">
                    <button id="startBtn" class="btn-primary">Start Verify</button>
                    <button id="resetBtn" class="btn-secondary" style="display: none;">Reset</button>
                </div>
            </div>

            <!-- Footer - hidden until verify starts -->
            <div class="auth-footer" style="display: none;">
                <p>Protected by Dexasign Verify</p>
            </div>
        `;

        // Store element references using ORIGINAL IDs
        this.elements = {
            webcam: this.container.querySelector('#webcam'),
            overlay: this.container.querySelector('#overlay'),
            overlayCtx: this.container.querySelector('#overlay').getContext('2d'),
            authStatus: this.container.querySelector('#authStatus'),
            gestureCount: this.container.querySelector('#gestureCount'),
            gestureInfo: this.container.querySelector('#gestureInfo'),
            startBtn: this.container.querySelector('#startBtn'),
            resetBtn: this.container.querySelector('#resetBtn'),
            loading: this.container.querySelector('#loadingOverlay'),
            
            // Challenge elements
            challengePrompt: this.container.querySelector('#challengePrompt'),
            challengeText: this.container.querySelector('#challengeText'),
            progressDots: this.container.querySelector('#progressDots'),
            dot0: this.container.querySelector('#dot0'),
            dot1: this.container.querySelector('#dot1'),
            dot2: this.container.querySelector('#dot2'),
            videoWrapper: this.container.querySelector('.video-wrapper'),
            instructionList: this.container.querySelector('#instructionList'),
            statusMessage: this.container.querySelector('#statusMessage'),
            authFooter: this.container.querySelector('.auth-footer')
        };

        this.startPointerTracking();
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        this.elements.startBtn.addEventListener('click', () => this.startCapture());
        this.elements.resetBtn.addEventListener('click', () => this.resetCapture());
    }

    /**
     * Start capture - EXACT logic from AuthScreen
     */
    async startCapture() {
        if (this._captureStarted) return;
        this._captureStarted = true;
        this.elements.startBtn.disabled = true;
        this.stopPointerTracking();
        this._pointerScore = this.computePointerScore();
        this._headlessSuspect = this.detectHeadlessBrowser();
        try {
            this.elements.authStatus.textContent = 'Starting camera...';
            
            // Start webcam
            this.webcamStream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480, facingMode: 'user' }
            });
            
            this.elements.webcam.srcObject = this.webcamStream;
            await this.elements.webcam.play();

            // Sync overlay canvas pixel size to actual video dimensions.
            // Without this, a webcam delivering a different aspect ratio (e.g. 16:9
            // instead of the requested 4:3) causes the CSS-stretched canvas to
            // mismatch its pixel dimensions, making touch bubbles appear as ovals.
            const syncOverlay = () => {
                if (this.elements.webcam.videoWidth && this.elements.webcam.videoHeight) {
                    const vw = this.elements.webcam.videoWidth;
                    const vh = this.elements.webcam.videoHeight;
                    // iOS front camera delivers portrait video when held upright
                    // (e.g. 480×640). Always use landscape so canvas aspect ratio
                    // matches the CSS-displayed video-wrapper and planets stay
                    // correctly positioned and proportioned.
                    this.elements.overlay.width  = Math.max(vw, vh);
                    this.elements.overlay.height = Math.min(vw, vh);
                }
            };
            if (this.elements.webcam.videoWidth) {
                syncOverlay();
            } else {
                this.elements.webcam.addEventListener('loadedmetadata', syncOverlay, { once: true });
            }

            // Update UI: transition from instructions view to active verify view
            this.elements.instructionList.style.display = 'none';
            this.elements.videoWrapper.style.display = '';
            // Pre-apply space-mission class so the black background shows before the
            // WebSocket challenge arrives — prevents a brief flash of the face feed
            if (this.preloadedConfigurations?.space_mission_theme === true) {
                this.elements.videoWrapper.classList.add('space-mission');
            }
            this.elements.challengePrompt.style.display = '';
            this.elements.statusMessage.style.display = '';
            this.elements.authFooter.style.display = '';
            this.elements.startBtn.style.display = 'none';
            this.elements.resetBtn.style.display = 'inline-block';
            this.elements.authStatus.textContent = this._mobileDragMode ? 'Get ready to touch and drag' : 'Show your hand';
            
            // Connect WebSocket
            await this.connectWebSocket();
            
        } catch (error) {
            console.error('Camera access failed:', error);
            this.elements.authStatus.textContent = 'Camera access denied';
            this.elements.authStatus.classList.add('dexasign-status-error');
            if (this.rejectPromise) {
                this.rejectPromise(error);
            }
        }
    }

    /**
     * Connect WebSocket - EXACT logic from AuthScreen
     */
    async connectWebSocket() {
        if (!this.applicationId) {
            console.error('❌ No application_id provided');
            this.elements.authStatus.textContent = 'Configuration error: Missing application ID';
            this.elements.authStatus.classList.add('dexasign-status-error');
            if (this.rejectPromise) {
                this.rejectPromise(new Error('Missing application_id'));
            }
            return;
        }
        
        const wsUrl = `${this.apiUrl}/ws/capture`;
        this.websocket = new WebSocket(wsUrl);
        
        this.websocket.onopen = () => {
            // Send application_id and bot detection signals
            this.websocket.send(JSON.stringify({
                application_id: this.applicationId,
                pointer_score: this._pointerScore,
                headless_suspect: this._headlessSuspect,
                mobile_touch_mode: this._mobileDragMode,
                is_mobile_device: this._isMobileDevice
            }));
            
            this.elements.authStatus.textContent = this._mobileDragMode
                ? 'Connected - Touch and drag to begin'
                : 'Connected - Show your hand';
        };
        
        this.websocket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            this.handleServerMessage(message);
        };
        
        this.websocket.onerror = (error) => {
            console.error('❌ WebSocket error:', error);
            this.elements.authStatus.textContent = 'Connection error';
            this.elements.authStatus.classList.add('dexasign-status-error');
        };
        
        this.websocket.onclose = () => {
        };
    }

    /**
     * Handle server messages - EXACT logic from AuthScreen
     */
    handleServerMessage(message) {
        switch (message.type) {
            case 'challenge':
                this.handleChallenge(message);
                break;
            case 'step_complete':
                this.handleStepComplete(message);
                break;
            case 'gesture_detected':
                this.handleGestureDetected(message);
                break;
            case 'no_hands':
                this.handleNoHands();
                break;
            case 'verification_complete':
                this.handleSuccess(message);
                break;
            case 'error':
                this.handleError(message);
                break;
        }
    }
    
    /**
     * Handle challenge data from server
     */
    handleChallenge(message) {
        // Apply configurations received from server
        if (message.configurations) {
            this.configurations = message.configurations;
            const animationEnabled = this.configurations.viewfinder_animation !== false;
            if (!animationEnabled && this.elements.videoWrapper) {
                this.elements.videoWrapper.classList.add('no-animation');
            }
        }

        if (message.mode === 'touch') {
            // Space Mission is a configuration flag on Touch mode apps
            if (this.configurations.space_mission_theme === true) {
                this.isSpaceMissionMode = true;
                this.spacePlanets = this.generateSpacePlanets();
                this.currentTargetPlanet = 0;
                this.contactFrameCount = 0;
                this._getSpaceImages();

                const dotCountSM = this.spacePlanets.length;
                if (this.elements.progressDots) {
                    this.elements.progressDots.innerHTML = Array.from({ length: dotCountSM }, (_, i) =>
                        `<div class="progress-dot" id="dot${i}" style="width: 16px; height: 16px; border-radius: 50%; background: #E5E7EB; border: 2px solid #D1D5DB; transition: all 0.3s ease;"></div>`
                    ).join('');
                    for (let i = 0; i < dotCountSM; i++) {
                        this.elements[`dot${i}`] = this.container.querySelector(`#dot${i}`);
                    }
                }
                if (this.elements.gestureInfo) {
                    this.elements.gestureInfo.style.display = 'none';
                }
                if (this.elements.videoWrapper) {
                    this.elements.videoWrapper.classList.add('space-mission');
                }
                const listSM = this.container.querySelector('#instructionList');
                if (listSM) {
                    const n = this.spacePlanets.length;
                    listSM.innerHTML = this._mobileDragMode ? `
                        <li>Touch and hold anywhere on the screen</li>
                        <li>${n} planets will appear &mdash; drag continuously to visit them in order</li>
                        <li>Do not lift your finger until you're done</li>
                        <li>Complete all ${n} planets to verify</li>
                    ` : `
                        <li>Hold your hand up so the camera can see it</li>
                        <li>${n} planets will appear &mdash; visit them in numbered order</li>
                        <li>Move your hand over each planet to visit it</li>
                        <li>Complete all ${n} planets to verify</li>
                    `;
                }
                const firstPlanet = this.spacePlanets[0];
                this.elements.challengeText.textContent = this._mobileDragMode
                    ? 'Touch and drag to the first planet'
                    : `Visit the ${firstPlanet.color} planet first`;
                this.startFrameCapture();
                this.drawSpaceMission();
                if (this._mobileDragMode) {
                    this.setupMobileDrag();
                } else {
                    this._startSpaceLoop();
                }
                return;
            }

            this.isTouchMode = true;
            this.touchBubbles = this.generateTouchBubbles();
            this.currentTargetBubble = 0;
            this.contactFrameCount = 0;

            // Rebuild progress dots to match bubble count
            const dotCount = this.touchBubbles.length;
            if (this.elements.progressDots) {
                this.elements.progressDots.innerHTML = Array.from({ length: dotCount }, (_, i) =>
                    `<div class="progress-dot" id="dot${i}" style="width: 16px; height: 16px; border-radius: 50%; background: #E5E7EB; border: 2px solid #D1D5DB; transition: all 0.3s ease;"></div>`
                ).join('');
                for (let i = 0; i < dotCount; i++) {
                    this.elements[`dot${i}`] = this.container.querySelector(`#dot${i}`);
                }
            }

            // Hide gesture counter — not relevant for touch
            if (this.elements.gestureInfo) {
                this.elements.gestureInfo.style.display = 'none';
            }

            // Swap instructions for touch mode — use configured count
            const list = this.container.querySelector('#instructionList');
            if (list) {
                const n = this.touchBubbles.length;
                const seq = Array.from({ length: n }, (_, i) => i + 1).join(' \u2192 ');
                list.innerHTML = this._mobileDragMode ? `
                    <li>Touch and hold anywhere on the screen</li>
                    <li>Drag your finger continuously to each bubble in order (${seq})</li>
                    <li>Do not lift your finger until you're done</li>
                    <li>Complete all bubbles to verify</li>
                ` : `
                    <li>Face the camera and position your hand in view</li>
                    <li>Numbered bubbles will appear on screen</li>
                    <li>Touch each bubble in order (${seq}) to pop it</li>
                    <li>Complete all bubbles to verify</li>
                `;
            }

            this.elements.challengeText.textContent = this._mobileDragMode
                ? 'Touch and drag to bubble 1'
                : 'Pop bubble 1 first';
            this.startFrameCapture();
            this.drawTouchBubbles();
            if (this._mobileDragMode) {
                this.setupMobileDrag();
            }
            return;
        }

        // Gesture mode
        this.challenges = message.challenges;
        this.currentStep = 0;

        // Rebuild progress dots to match actual challenge count
        const gestDotCount = this.challenges.length;
        if (this.elements.progressDots) {
            this.elements.progressDots.innerHTML = Array.from({ length: gestDotCount }, (_, i) =>
                `<div class="progress-dot" id="dot${i}" style="width: 16px; height: 16px; border-radius: 50%; background: #E5E7EB; border: 2px solid #D1D5DB; transition: all 0.3s ease;"></div>`
            ).join('');
            for (let i = 0; i < gestDotCount; i++) {
                this.elements[`dot${i}`] = this.container.querySelector(`#dot${i}`);
            }
        }

        // Swap instructions for gesture mode
        const listG = this.container.querySelector('#instructionList');
        if (listG) {
            listG.innerHTML = `
                <li>Face the camera and position your hand in view</li>
                <li>Match the gesture sequence described</li>
                <li>Hold each gesture steady until it's recognized</li>
                <li>Complete all gestures to verify</li>
            `;
        }

        // Display first challenge
        this.updateChallengeDisplay();

        // Start frame capture now that we have challenges
        this.startFrameCapture();
    }
    
    /**
     * Handle step completion
     */
    handleStepComplete(message) {
        // Touch mode: display is already updated by popBubble() animation
        if (this.isTouchMode) return;

        const { step, total } = message;
        this.currentStep = step;
        
        // Update progress display
        this.updateChallengeDisplay();
    }
    
    /**
     * Update challenge text and progress dots
     */
    updateChallengeDisplay() {
        if (this.isSpaceMissionMode) {
            const completedCount = this.currentTargetPlanet;
            const total = this.spacePlanets.length;
            for (let i = 0; i < total; i++) {
                const dot = this.elements[`dot${i}`];
                if (dot) {
                    if (i < completedCount) {
                        const planet = this.spacePlanets[i];
                        dot.style.background = SPACE_PLANET_CSS[planet.color];
                        dot.style.borderColor = SPACE_PLANET_CSS[planet.color];
                        dot.style.boxShadow = `0 0 0 3px ${SPACE_PLANET_CSS[planet.color]}33`;
                    } else {
                        dot.style.background = '#E5E7EB';
                        dot.style.borderColor = '#D1D5DB';
                        dot.style.boxShadow = 'none';
                    }
                }
            }
            if (completedCount >= total) {
                this.elements.challengeText.textContent = 'All planets visited!';
            } else {
                const planet = this.spacePlanets[completedCount];
                this.elements.challengeText.textContent =
                    `Visit the ${planet.color} planet (${completedCount + 1}/${total})`;
            }
            return;
        }

        if (this.isTouchMode) {
            const completedCount = this.currentTargetBubble;
            const total = this.touchBubbles.length;
            for (let i = 0; i < total; i++) {
                const dot = this.elements[`dot${i}`];
                if (dot) {
                    if (i < completedCount) {
                        dot.style.background = '#10B981';
                        dot.style.borderColor = '#059669';
                        dot.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    } else {
                        dot.style.background = '#E5E7EB';
                        dot.style.borderColor = '#D1D5DB';
                        dot.style.boxShadow = 'none';
                    }
                }
            }
            if (completedCount >= total) {
                this.elements.challengeText.textContent = 'All bubbles popped!';
            } else {
                this.elements.challengeText.textContent = `Pop bubble ${completedCount + 1}`;
            }
            return;
        }

        if (this.currentStep >= this.challenges.length) {
            // All challenges complete — light up all dots green first
            for (let i = 0; i < this.challenges.length; i++) {
                const dot = this.elements[`dot${i}`];
                if (dot) {
                    dot.style.background = '#10B981';
                    dot.style.borderColor = '#059669';
                    dot.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                }
            }
            this.elements.challengeText.textContent = 'All steps completed!';
            return;
        }
        
        const challenge = this.challenges[this.currentStep];
        
        // Generate simple, direct instruction
        const fingerNames = challenge.fingers
            .map(f => f.charAt(0).toUpperCase() + f.slice(1))
            .join(' + ');
        
        this.elements.challengeText.textContent = `${challenge.hand} hand: ${fingerNames}`;
        
        // Update progress dots - match onboarding style exactly
        for (let i = 0; i < this.challenges.length; i++) {
            const dot = this.elements[`dot${i}`];
            if (dot) {
                if (i < this.currentStep) {
                    // Completed - green with border and shadow like onboarding
                    dot.style.background = '#10B981';
                    dot.style.borderColor = '#059669';
                    dot.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                } else {
                    // Not completed - gray with border
                    dot.style.background = '#E5E7EB';
                    dot.style.borderColor = '#D1D5DB';
                    dot.style.boxShadow = 'none';
                }
            }
        }
    }

    /**
     * Handle gesture detected - EXACT logic from AuthScreen
     */
    handleGestureDetected(message) {
        if (this._mobileDragMode) return; // contact handled by pointer events, not hand landmarks
        if (this.isTouchMode) {
            // Touch mode: use landmarks to check fingertip contact with target bubble
            if (message.landmarks && message.landmarks.length > 0) {
                this.checkBubbleContact(message.landmarks);
            }
            // Redraw bubbles unless a pop animation is already running
            if (!this.touchBubbles.some(b => b.state === 'popping')) {
                this.drawTouchBubbles();
            }
            return;
        }

        if (this.isSpaceMissionMode) {
            if (message.landmarks && message.landmarks.length > 0) {
                this._updateShipPosition(message.landmarks);
                this.checkPlanetContact(message.landmarks);
            } else {
                this._shipTargetX = null;
                this._shipTargetY = null;
            }
            // Drawing is handled by the 60fps RAF loop
            return;
        }

        this.hasStartedGestures = true;
        this.noHandsFrameCount = 0;
        
        // Flash effect when new gesture captured (if animation enabled)
        const newCount = message.gesture_count || 0;
        const animationEnabled = this.configurations.viewfinder_animation !== false;
        if (newCount > this.gesturesRecorded && animationEnabled) {
            this.elements.videoWrapper.classList.add('gesture-flash');
            setTimeout(() => {
                this.elements.videoWrapper.classList.remove('gesture-flash');
            }, 750);
        }
        
        this.gesturesRecorded = newCount;
        this.elements.gestureCount.textContent = this.gesturesRecorded;
        
        if (message.current_gesture) {
            this.elements.authStatus.textContent = 'Capturing gestures...';
        }
        
        // Don't draw landmarks - just clear canvas
        this.clearCanvas();
    }

    /**
     * Handle no hands detected - For verify, don't auto-submit
     */
    handleNoHands() {
        if (this._mobileDragMode) return; // no hand tracking in mobile drag mode
        if (this.isTouchMode) {
            // Reset contact counter when hand leaves view
            this.contactFrameCount = 0;
            if (!this.touchBubbles.some(b => b.state === 'popping')) {
                this.drawTouchBubbles();
            }
            return;
        }

        if (this.isSpaceMissionMode) {
            this._shipTargetX = null;
            this._shipTargetY = null;
            // Drawing is handled by the 60fps RAF loop
            return;
        }

        if (!this.hasStartedGestures) {
            this.elements.authStatus.textContent = 'Show your hand';
            return;
        }
        
        // Don't increment noHandsFrameCount or auto-submit for verify
        // User must match all challenges - backend will send verification_complete
        
        this.clearCanvas();
    }

    /**
     * Submit capture - EXACT logic from AuthScreen
     */
    submitCapture() {
        if (this.isCapturing) return;
        
        this.isCapturing = true;
        this.noHandsFrameCount = 0;
        
        this.stopFrameCapture();
        
        this.elements.loading.style.display = 'flex';
        this.elements.authStatus.textContent = '';
        this.clearCanvas();
        
        // Send completion message (like auth's submit)
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(JSON.stringify({
                type: 'complete',
                gesture_count: this.gesturesRecorded
            }));
        }
    }

    /**
     * Handle success - EXACT logic from AuthScreen
     */
    handleSuccess(message) {
        this.stopFrameCapture();
        
        if (this.websocket) {
            this.websocket.close();
        }
        
        this.elements.loading.style.display = 'none';
        this.elements.authStatus.textContent = 'Verified successfully!';
        this.elements.authStatus.classList.add('dexasign-status-success');
        
        // Extract token from message
        const token = message.token;
        const expiresIn = message.expires_in || 300; // default 5 minutes
        
        if (this.resolvePromise) {
            this.resolvePromise({
                success: true,
                token: token,
                expires_in: expiresIn,
                gesture_count: this.gesturesRecorded,
                message: message
            });
        }
        
        if (this.onSuccess) {
            this.onSuccess({
                success: true,
                token: token,
                expires_in: expiresIn,
                gesture_count: this.gesturesRecorded
            });
        }
    }

    /**
     * Handle error - EXACT logic from AuthScreen
     */
    handleError(message) {
        console.error('Server error:', message);
        this.elements.loading.style.display = 'none';
        this.elements.authStatus.textContent = message.message || 'An error occurred';
        this.elements.authStatus.classList.add('dexasign-status-error');
        this.isCapturing = false;
        
        if (this.rejectPromise) {
            this.rejectPromise(new Error(message.message || 'Verification error'));
        }
        
        if (this.onError) {
            this.onError(new Error(message.message || 'Verification error'));
        }
    }

    /**
     * Frame capture - EXACT logic from AuthScreen
     */
    startFrameCapture() {
        this.frameInterval = setInterval(() => this.captureAndSendFrame(), 1000 / this.FRAME_RATE);
    }

    stopFrameCapture() {
        if (this.frameInterval) {
            clearInterval(this.frameInterval);
            this.frameInterval = null;
        }
    }

    captureAndSendFrame() {
        if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
            console.warn('⚠️ Cannot send frame - WebSocket not ready');
            return;
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = this.elements.webcam.videoWidth;
        canvas.height = this.elements.webcam.videoHeight;
        const ctx = canvas.getContext('2d');
        
        // Mirror frame (like auth) - backend expects mirrored frames
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(this.elements.webcam, 0, 0);
        
        const imageData = canvas.toDataURL('image/jpeg', 0.8);
        
        this.websocket.send(JSON.stringify({
            type: 'frame',
            data: imageData
        }));
    }

    /**
     * Draw hand landmarks - EXACT logic from AuthScreen
     */
    drawHandLandmarks(landmarks) {
        this.clearCanvas();
        
        if (!landmarks || landmarks.length === 0) return;
        
        const canvas = this.elements.overlay;
        const ctx = this.elements.overlayCtx;
        const video = this.elements.webcam;
        
        const scaleX = canvas.width / video.videoWidth;
        const scaleY = canvas.height / video.videoHeight;
        
        landmarks.forEach(handLandmarks => {
            ctx.strokeStyle = '#0A2540';
            ctx.lineWidth = 2;
            ctx.fillStyle = '#0A2540';
            
            const connections = [
                [0,1],[1,2],[2,3],[3,4],
                [0,5],[5,6],[6,7],[7,8],
                [0,9],[9,10],[10,11],[11,12],
                [0,13],[13,14],[14,15],[15,16],
                [0,17],[17,18],[18,19],[19,20]
            ];
            
            connections.forEach(([start, end]) => {
                ctx.beginPath();
                ctx.moveTo(
                    handLandmarks[start].x * canvas.width * scaleX,
                    handLandmarks[start].y * canvas.height * scaleY
                );
                ctx.lineTo(
                    handLandmarks[end].x * canvas.width * scaleX,
                    handLandmarks[end].y * canvas.height * scaleY
                );
                ctx.stroke();
            });
            
            handLandmarks.forEach(point => {
                ctx.beginPath();
                ctx.arc(
                    point.x * canvas.width * scaleX,
                    point.y * canvas.height * scaleY,
                    4, 0, 2 * Math.PI
                );
                ctx.fill();
            });
        });
    }

    // ─── Touch Mode Methods ────────────────────────────────────────────────

    generateTouchBubbles() {
        const count = Math.min(5, Math.max(3, this.configurations.touch_bubble_count ?? 4));
        // 6 evenly-spaced zones in a 3-col × 2-row grid (normalised coords)
        // Spacing scaled to 1.5x from grid centre (0.50, 0.48)
        const zones = [
            { x: 0.08, y: 0.18 }, { x: 0.50, y: 0.18 }, { x: 0.92, y: 0.18 },
            { x: 0.08, y: 0.78 }, { x: 0.50, y: 0.78 }, { x: 0.92, y: 0.78 },
        ];
        // Fisher-Yates shuffle all 6, pick first `count`
        for (let i = zones.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [zones[i], zones[j]] = [zones[j], zones[i]];
        }
        const selected = zones.slice(0, count);
        // Shuffle selected so bubble #1 is at a random position
        for (let i = selected.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [selected[i], selected[j]] = [selected[j], selected[i]];
        }
        return selected.map((zone, i) => ({
            index: i + 1,
            x: Math.max(0.08, Math.min(0.92, zone.x + (Math.random() - 0.5) * 0.05)),
            y: Math.max(0.10, Math.min(0.90, zone.y + (Math.random() - 0.5) * 0.05)),
            radius: 40, // px on a 640-wide canvas
            state: 'visible', // 'visible' | 'popping' | 'popped'
            popProgress: 0,
            rippleProgress: 0,
        }));
    }

    drawTouchBubbles() {
        const canvas = this.elements.overlay;
        const ctx = this.elements.overlayCtx;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        for (const bubble of this.touchBubbles) {
            if (bubble.state === 'popped') continue;

            const cx = bubble.x * canvas.width;
            const cy = bubble.y * canvas.height;

            if (bubble.state === 'popping') {
                // Expanding ripple ring
                if (bubble.rippleProgress < 1) {
                    const rippleR = bubble.radius * (1 + bubble.rippleProgress * 0.9);
                    ctx.save();
                    ctx.globalAlpha = (1 - bubble.rippleProgress) * 0.75;
                    ctx.beginPath();
                    ctx.arc(cx, cy, rippleR, 0, Math.PI * 2);
                    ctx.strokeStyle = 'rgba(10, 170, 240, 0.9)';
                    ctx.lineWidth = 3;
                    ctx.stroke();
                    ctx.restore();
                }
                // Shrinking bubble body
                const scale = 1 - bubble.popProgress;
                if (scale > 0) {
                    ctx.save();
                    ctx.globalAlpha = scale;
                    ctx.beginPath();
                    ctx.arc(cx, cy, bubble.radius * scale, 0, Math.PI * 2);
                    ctx.fillStyle = 'rgba(200, 210, 220, 0.4)';
                    ctx.fill();
                    ctx.strokeStyle = 'rgba(150, 160, 170, 0.6)';
                    ctx.lineWidth = 2;
                    ctx.stroke();
                    ctx.restore();
                }
            } else {
                // Visible state — glossy 3D sphere
                const isTarget = bubble.index === this.currentTargetBubble + 1;
                const r = bubble.radius;
                ctx.save();

                // Drop shadow
                ctx.shadowColor = isTarget ? 'rgba(0, 80, 180, 0.40)' : 'rgba(0, 0, 0, 0.18)';
                ctx.shadowBlur = r * 0.55;
                ctx.shadowOffsetY = r * 0.20;

                // Main sphere gradient (off-centre toward top-left for 3D depth)
                const grad = ctx.createRadialGradient(
                    cx - r * 0.22, cy - r * 0.22, r * 0.04,
                    cx, cy, r
                );
                if (isTarget) {
                    grad.addColorStop(0,    'rgba(220, 248, 255, 0.72)');
                    grad.addColorStop(0.30, 'rgba(80,  205, 255, 0.62)');
                    grad.addColorStop(0.70, 'rgba(10,  160, 235, 0.68)');
                    grad.addColorStop(1,    'rgba(0,   110, 195, 0.75)');
                } else {
                    grad.addColorStop(0,   'rgba(235, 242, 248, 0.55)');
                    grad.addColorStop(0.4, 'rgba(195, 208, 220, 0.45)');
                    grad.addColorStop(1,   'rgba(145, 162, 178, 0.50)');
                }
                ctx.beginPath();
                ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.fillStyle = grad;
                ctx.fill();

                // Reset shadow before layering specular
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.shadowOffsetY = 0;

                // Specular highlight (top-left gloss)
                const sx = cx - r * 0.28;
                const sy = cy - r * 0.30;
                const specGrad = ctx.createRadialGradient(sx, sy, 0, sx, sy, r * 0.58);
                specGrad.addColorStop(0,   'rgba(255, 255, 255, 0.65)');
                specGrad.addColorStop(0.45,'rgba(255, 255, 255, 0.15)');
                specGrad.addColorStop(1,   'rgba(255, 255, 255, 0.00)');
                ctx.beginPath();
                ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.fillStyle = specGrad;
                ctx.fill();

                // Number
                ctx.fillStyle = isTarget ? 'rgba(0, 55, 130, 0.92)' : 'rgba(80, 100, 118, 0.90)';
                ctx.font = `bold ${Math.round(r * 0.75)}px -apple-system, BlinkMacSystemFont, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.shadowColor = 'rgba(255, 255, 255, 0.55)';
                ctx.shadowBlur = 5;
                ctx.fillText(bubble.index.toString(), cx, cy);
                ctx.restore();
            }
        }
    }

    checkBubbleContact(allHandLandmarks) {
        if (this.currentTargetBubble >= this.touchBubbles.length) return;
        const bubble = this.touchBubbles[this.currentTargetBubble];
        if (bubble.state !== 'visible') return;

        const canvas = this.elements.overlay;
        // Use all 21 landmarks — any part of the hand can pop a bubble.
        // Canvas is CSS scaleX(-1) mirrored, matching the mirrored frame sent to server,
        // so landmark x maps directly without inversion.
        let inContact = false;

        for (const handLandmarks of allHandLandmarks) {
            for (const lm of handLandmarks) {
                const lmX = lm.x * canvas.width;
                const lmY = lm.y * canvas.height;
                const bubbleCX = bubble.x * canvas.width;
                const bubbleCY = bubble.y * canvas.height;
                const dist = Math.sqrt((lmX - bubbleCX) ** 2 + (lmY - bubbleCY) ** 2);
                if (dist < bubble.radius * 1.3) {
                    inContact = true;
                    break;
                }
            }
            if (inContact) break;
        }

        if (inContact) {
            this.contactFrameCount++;
            if (this.contactFrameCount >= this.CONTACT_FRAMES_REQUIRED) {
                this.contactFrameCount = 0;
                this.popBubble(this.currentTargetBubble);
            }
        } else {
            this.contactFrameCount = Math.max(0, this.contactFrameCount - 1);
        }
    }

    popBubble(bubbleIdx) {
        const bubble = this.touchBubbles[bubbleIdx];
        if (bubble.state !== 'visible') return;

        bubble.state = 'popping';
        bubble.popProgress = 0;
        bubble.rippleProgress = 0;

        // Flash the viewfinder (same as gesture mode, respects animation config)
        const animationEnabled = this.configurations.viewfinder_animation !== false;
        if (animationEnabled && this.elements.videoWrapper) {
            this.elements.videoWrapper.classList.add('gesture-flash');
            setTimeout(() => this.elements.videoWrapper.classList.remove('gesture-flash'), 750);
        }

        // Advance target immediately so dots + text update right away
        this.currentTargetBubble++;
        this.updateChallengeDisplay();

        // Show loading overlay after final bubble pop
        if (this.currentTargetBubble >= this.touchBubbles.length) {
            setTimeout(() => {
                this.elements.loading.style.display = 'flex';
            }, 400);
        }

        // Notify server
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            const payload = {
                type: 'bubble_pop',
                bubble_index: bubble.index
            };
            // On the final pop in Mobile Mode, attach a pointer score computed from
            // the actual touch-drag session (ambient pre-capture score is empty on mobile)
            if (this._mobileDragMode && this.currentTargetBubble >= this.touchBubbles.length) {
                payload.pointer_score = this.computePointerScore(this._mobileDragEvents, true);
            }
            this.websocket.send(JSON.stringify(payload));
        }

        // Animate with requestAnimationFrame
        const animatePop = () => {
            bubble.popProgress = Math.min(1, bubble.popProgress + 0.10);
            bubble.rippleProgress = Math.min(1, bubble.rippleProgress + 0.12);
            this.drawTouchBubbles();
            if (bubble.popProgress < 1) {
                requestAnimationFrame(animatePop);
            } else {
                bubble.state = 'popped';
                this.drawTouchBubbles();
            }
        };
        requestAnimationFrame(animatePop);
    }

    // ─── Space Mission Methods ─────────────────────────────────────────────────

    _getSpaceImages() {
        if (this._spaceImagesCache) return this._spaceImagesCache;
        // Images load async — until decoded, drawSpaceMission() falls back to a flat
        // color circle. Trigger a redraw as each image finishes loading so the sprite
        // appears immediately instead of only on the next touch/RAF-driven redraw.
        const make = (src) => {
            const img = new Image();
            img.onload = () => {
                if (this.isSpaceMissionMode) this.drawSpaceMission();
            };
            img.src = src;
            return img;
        };
        this._spaceImagesCache = {
            blue:   make(bluePlanetSrc),
            green:  make(greenPlanetSrc),
            purple: make(purplePlanetSrc),
            red:    make(redPlanetSrc),
            tan:    make(tanPlanetSrc),
            ship:   make(shipSrc),
        };
        return this._spaceImagesCache;
    }

    generateSpacePlanets() {
        const count = Math.min(5, Math.max(3, this.configurations.touch_bubble_count ?? 4));
        const zones = [
            { x: 0.15, y: 0.22 }, { x: 0.50, y: 0.18 }, { x: 0.85, y: 0.22 },
            { x: 0.15, y: 0.72 }, { x: 0.50, y: 0.75 }, { x: 0.85, y: 0.72 },
        ];
        // Shuffle zones and pick `count`
        for (let i = zones.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [zones[i], zones[j]] = [zones[j], zones[i]];
        }
        const selected = zones.slice(0, count);

        // Assign a random subset of planet colors
        const colors = [...SPACE_PLANET_COLORS].slice(0, count);
        for (let i = colors.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [colors[i], colors[j]] = [colors[j], colors[i]];
        }

        // Shuffle positions so visit order 1 doesn't always land in the same zone
        for (let i = selected.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [selected[i], selected[j]] = [selected[j], selected[i]];
        }

        // Generate a random star field once per session
        this.spaceStars = Array.from({ length: 50 }, () => ({
            x: Math.random(), y: Math.random(),
            r: Math.random() < 0.25 ? 1.4 : 0.8,
            alpha: 0.3 + Math.random() * 0.5,
        }));

        return selected.map((zone, i) => ({
            index: i + 1,
            x: Math.max(0.12, Math.min(0.88, zone.x + (Math.random() - 0.5) * 0.04)),
            y: Math.max(0.12, Math.min(0.80, zone.y + (Math.random() - 0.5) * 0.04)),
            radius: 40,
            color: colors[i],
            state: 'visible',     // 'visible' | 'visiting' | 'visited'
            popProgress: 0,
            rippleProgress: 0,
        }));
    }

    _updateShipPosition(allLandmarks) {
        if (!allLandmarks || allLandmarks.length === 0) {
            this._shipTargetX = null;
            this._shipTargetY = null;
            return;
        }
        const canvas = this.elements.overlay;
        // Landmark 9 = middle MCP joint — reliable palm-centre proxy
        const hand = allLandmarks[0];
        const anchor = hand[9] || hand[0];
        this._shipTargetX = anchor.x * canvas.width;
        this._shipTargetY = anchor.y * canvas.height;
    }

    _startSpaceLoop() {
        this._stopSpaceLoop();
        const loop = () => {
            if (!this.isSpaceMissionMode) {
                this._spaceRafId = null;
                return;
            }
            // Lerp render position toward server-reported target for smooth movement
            const LERP = 0.35;
            if (this._shipTargetX !== null) {
                this._shipX = this._shipX === null
                    ? this._shipTargetX
                    : this._shipX + (this._shipTargetX - this._shipX) * LERP;
                this._shipY = this._shipY === null
                    ? this._shipTargetY
                    : this._shipY + (this._shipTargetY - this._shipY) * LERP;
            } else {
                this._shipX = null;
                this._shipY = null;
            }
            if (!this.spacePlanets.some(p => p.state === 'visiting')) {
                this.drawSpaceMission();
            }
            this._spaceRafId = requestAnimationFrame(loop);
        };
        this._spaceRafId = requestAnimationFrame(loop);
    }

    _stopSpaceLoop() {
        if (this._spaceRafId) {
            cancelAnimationFrame(this._spaceRafId);
            this._spaceRafId = null;
        }
    }

    drawSpaceMission() {
        const canvas = this.elements.overlay;
        const ctx = this.elements.overlayCtx;
        const imgs = this._getSpaceImages();

        // Black space background
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Star field
        ctx.save();
        for (const star of this.spaceStars) {
            ctx.globalAlpha = star.alpha;
            ctx.fillStyle = '#fff';
            ctx.beginPath();
            ctx.arc(star.x * canvas.width, star.y * canvas.height, star.r, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();

        // Draw planets
        for (const planet of this.spacePlanets) {
            if (planet.state === 'visited') continue;
            const cx = planet.x * canvas.width;
            const cy = planet.y * canvas.height;
            const img = imgs[planet.color];
            const isTarget = planet.index === this.currentTargetPlanet + 1;

            if (planet.state === 'visiting') {
                // Scale up and fade out on visit
                const scale = 1 + planet.popProgress * 0.7;
                ctx.save();
                ctx.globalAlpha = 1 - planet.popProgress;
                // Expanding ring
                ctx.beginPath();
                ctx.arc(cx, cy, planet.radius * (1 + planet.rippleProgress * 1.8), 0, Math.PI * 2);
                ctx.strokeStyle = SPACE_PLANET_CSS[planet.color];
                ctx.lineWidth = 3;
                ctx.stroke();
                // Planet image scaling out
                const drawR = planet.radius * scale;
                if (img.complete && img.naturalWidth > 0) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(cx, cy, drawR, 0, Math.PI * 2);
                    ctx.clip();
                    const aspV = img.naturalWidth / img.naturalHeight;
                    const dvh = drawR * 2, dvw = dvh * aspV;
                    ctx.drawImage(img, cx - dvw / 2, cy - drawR, dvw, dvh);
                    ctx.restore();
                }
                ctx.restore();
            } else {
                ctx.save();
                // Pulsing glow ring on current target
                if (isTarget) {
                    const pulse = 0.5 + 0.5 * Math.sin(Date.now() * 0.0045);
                    ctx.beginPath();
                    ctx.arc(cx, cy, planet.radius + 10 + pulse * 7, 0, Math.PI * 2);
                    ctx.strokeStyle = SPACE_PLANET_CSS[planet.color];
                    ctx.lineWidth = 2.5;
                    ctx.globalAlpha = 0.5 + pulse * 0.4;
                    ctx.stroke();
                    ctx.globalAlpha = 1;
                } else {
                    ctx.globalAlpha = 0.68;
                }

                const r = planet.radius;
                if (img.complete && img.naturalWidth > 0) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(cx, cy, r, 0, Math.PI * 2);
                    ctx.clip();
                    const aspP = img.naturalWidth / img.naturalHeight;
                    const dph = r * 2, dpw = dph * aspP;
                    ctx.drawImage(img, cx - dpw / 2, cy - r, dpw, dph);
                    ctx.restore();
                } else {
                    // Fallback circle if image hasn't loaded yet
                    ctx.beginPath();
                    ctx.arc(cx, cy, r, 0, Math.PI * 2);
                    ctx.fillStyle = SPACE_PLANET_CSS[planet.color];
                    ctx.fill();
                }
                ctx.restore();

                // Visit-order badge (number in dark circle, top-right of planet)
                ctx.save();
                const bx = cx + r * 0.58, by = cy - r * 0.58;
                ctx.beginPath();
                ctx.arc(bx, by, 11, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(0,0,0,0.72)';
                ctx.fill();
                ctx.fillStyle = '#fff';
                ctx.font = 'bold 12px -apple-system, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(planet.index.toString(), bx, by);
                ctx.restore();
            }
        }

        // Planet order strip at bottom
        this._drawPlanetOrderStrip(ctx, canvas);

        // Spaceship at hand position
        if (this._shipX !== null && this._shipY !== null) {
            const shipImg = imgs.ship;
            const shipSize = 36;
            if (shipImg.complete && shipImg.naturalWidth > 0) {
                ctx.save();
                ctx.drawImage(shipImg, this._shipX - shipSize / 2, this._shipY - shipSize / 2, shipSize, shipSize);
                ctx.restore();
            }
        }
    }

    _drawPlanetOrderStrip(ctx, canvas) {
        const sorted = [...this.spacePlanets].sort((a, b) => a.index - b.index);
        const n = sorted.length;
        const itemSize = 26;
        const arrowW = 18;
        const totalWidth = n * itemSize + (n - 1) * arrowW;
        const startX = (canvas.width - totalWidth) / 2;
        const cy = canvas.height - 28;
        const imgs = this._getSpaceImages();

        // Background pill
        ctx.save();
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        const pad = 14;
        const pillX = startX - pad, pillY = cy - itemSize / 2 - 7;
        const pillW = totalWidth + pad * 2, pillH = itemSize + 14;
        const pillR = pillH / 2;
        ctx.beginPath();
        ctx.moveTo(pillX + pillR, pillY);
        ctx.lineTo(pillX + pillW - pillR, pillY);
        ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillR, pillR);
        ctx.lineTo(pillX + pillW, pillY + pillH - pillR);
        ctx.arcTo(pillX + pillW, pillY + pillH, pillX + pillW - pillR, pillY + pillH, pillR);
        ctx.lineTo(pillX + pillR, pillY + pillH);
        ctx.arcTo(pillX, pillY + pillH, pillX, pillY + pillH - pillR, pillR);
        ctx.lineTo(pillX, pillY + pillR);
        ctx.arcTo(pillX, pillY, pillX + pillR, pillY, pillR);
        ctx.closePath();
        ctx.fill();

        for (let i = 0; i < n; i++) {
            const planet = sorted[i];
            const itemCX = startX + i * (itemSize + arrowW) + itemSize / 2;
            const isCompleted = planet.state === 'visited' || planet.state === 'visiting';
            const isCurrent = planet.index === this.currentTargetPlanet + 1;
            const img = imgs[planet.color];

            if (isCurrent) {
                ctx.beginPath();
                ctx.arc(itemCX, cy, itemSize / 2 + 4, 0, Math.PI * 2);
                ctx.strokeStyle = SPACE_PLANET_CSS[planet.color];
                ctx.lineWidth = 2;
                ctx.globalAlpha = 0.9;
                ctx.stroke();
            }

            ctx.globalAlpha = isCompleted ? 0.28 : (isCurrent ? 1.0 : 0.65);
            if (img.complete && img.naturalWidth > 0) {
                ctx.save();
                ctx.beginPath();
                ctx.arc(itemCX, cy, itemSize / 2, 0, Math.PI * 2);
                ctx.clip();
                const aspS = img.naturalWidth / img.naturalHeight;
                const dsh = itemSize, dsw = dsh * aspS;
                ctx.drawImage(img, itemCX - dsw / 2, cy - itemSize / 2, dsw, dsh);
                ctx.restore();
            } else {
                ctx.beginPath();
                ctx.arc(itemCX, cy, itemSize / 2, 0, Math.PI * 2);
                ctx.fillStyle = SPACE_PLANET_CSS[planet.color];
                ctx.fill();
            }

            // Arrow separator between items
            if (i < n - 1) {
                ctx.globalAlpha = 0.45;
                ctx.fillStyle = '#ccc';
                ctx.font = '11px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('›', itemCX + itemSize / 2 + arrowW / 2, cy);
            }
            ctx.globalAlpha = 1;
        }
        ctx.restore();
    }

    checkPlanetContact(allHandLandmarks) {
        if (this.currentTargetPlanet >= this.spacePlanets.length) return;
        const planet = this.spacePlanets[this.currentTargetPlanet];
        if (planet.state !== 'visible') return;

        // Use the rendered ship position so contact is visually accurate —
        // checking all 21 landmarks caused lateral ones (thumb, pinky) to fire
        // well before the ship was visually close to the planet.
        if (this._shipX === null || this._shipY === null) return;

        const canvas = this.elements.overlay;
        const px = planet.x * canvas.width;
        const py = planet.y * canvas.height;
        const dist = Math.sqrt((this._shipX - px) ** 2 + (this._shipY - py) ** 2);
        const inContact = dist < planet.radius * 1.1;

        if (inContact) {
            this.contactFrameCount++;
            if (this.contactFrameCount >= this.CONTACT_FRAMES_REQUIRED) {
                this.contactFrameCount = 0;
                this.visitPlanet(this.currentTargetPlanet);
            }
        } else {
            this.contactFrameCount = Math.max(0, this.contactFrameCount - 1);
        }
    }

    visitPlanet(planetIdx) {
        const planet = this.spacePlanets[planetIdx];
        if (planet.state !== 'visible') return;

        planet.state = 'visiting';
        planet.popProgress = 0;
        planet.rippleProgress = 0;

        this.currentTargetPlanet++;
        this.updateChallengeDisplay();

        if (this.currentTargetPlanet >= this.spacePlanets.length) {
            setTimeout(() => {
                this.elements.loading.style.display = 'flex';
            }, 450);
        }

        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            const payload = {
                type: 'bubble_pop',
                bubble_index: planet.index,
            };
            // On the final visit in Mobile Mode, attach a pointer score computed from
            // the actual touch-drag session (ambient pre-capture score is empty on mobile)
            if (this._mobileDragMode && this.currentTargetPlanet >= this.spacePlanets.length) {
                payload.pointer_score = this.computePointerScore(this._mobileDragEvents, true);
            }
            this.websocket.send(JSON.stringify(payload));
        }

        const animateVisit = () => {
            planet.popProgress  = Math.min(1, planet.popProgress  + 0.075);
            planet.rippleProgress = Math.min(1, planet.rippleProgress + 0.09);
            this.drawSpaceMission();
            if (planet.popProgress < 1) {
                requestAnimationFrame(animateVisit);
            } else {
                planet.state = 'visited';
                this.drawSpaceMission();
            }
        };
        requestAnimationFrame(animateVisit);
    }

    // ─── Mobile Touch-Drag Methods ─────────────────────────────────────────────

    /**
     * Attach pointer listeners that drive the touch-drag interaction used in
     * place of hand tracking on mobile. Contact is checked against the
     * finger's position rather than MediaPipe hand landmarks.
     */
    setupMobileDrag() {
        if (this._mobileDragListenersAttached) return;
        this._mobileDragListenersAttached = true;

        const canvas = this.elements.overlay;
        // The overlay canvas has `pointer-events: none` by default (it's normally
        // just a drawing surface) — enable it here so touch/pointer events reach
        // our listeners instead of falling through to the page and scrolling it.
        canvas.style.pointerEvents = 'auto';
        canvas.style.touchAction = 'none';

        const getCanvasPoint = (e) => {
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;
            // Unlike the <video> element, the overlay canvas itself is NOT CSS-mirrored
            // (only the video has scaleX(-1)) — bubble/planet coords are drawn directly
            // in screen space, so the finger position maps 1:1 without flipping x.
            const x = (e.clientX - rect.left) * scaleX;
            const y = (e.clientY - rect.top) * scaleY;
            return { x, y };
        };

        const onPointerDown = (e) => {
            if (!this._mobileDragMode || this._dragActive) return;
            e.preventDefault();
            this._dragActive = true;
            this._dragPointerId = e.pointerId;
            try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
            const { x, y } = getCanvasPoint(e);
            this._shipTargetX = x;
            this._shipTargetY = y;
            this._shipX = x;
            this._shipY = y;
            this._mobileDragEvents.push({
                x: e.clientX, y: e.clientY,
                pressure: e.pressure, width: e.width, height: e.height,
                timestamp: e.timeStamp, pointerType: e.pointerType
            });
            this._startMobileDragLoop();
        };

        const onPointerMove = (e) => {
            if (!this._dragActive || e.pointerId !== this._dragPointerId) return;
            e.preventDefault();
            const { x, y } = getCanvasPoint(e);
            this._shipTargetX = x;
            this._shipTargetY = y;
            this._mobileDragEvents.push({
                x: e.clientX, y: e.clientY,
                pressure: e.pressure, width: e.width, height: e.height,
                timestamp: e.timeStamp, pointerType: e.pointerType
            });
        };

        const endDrag = (e) => {
            if (!this._dragActive || (e.pointerId !== undefined && e.pointerId !== this._dragPointerId)) return;
            this._dragActive = false;
            this._dragPointerId = null;
            this._stopMobileDragLoop();
            this._shipTargetX = null;
            this._shipTargetY = null;
            this._shipX = null;
            this._shipY = null;

            const total = this.isSpaceMissionMode ? this.spacePlanets.length : this.touchBubbles.length;
            const completed = this.isSpaceMissionMode ? this.currentTargetPlanet : this.currentTargetBubble;
            if (completed < total) {
                this.resetMobileDragProgress();
            }
        };

        canvas.addEventListener('pointerdown', onPointerDown, { passive: false });
        canvas.addEventListener('pointermove', onPointerMove, { passive: false });
        canvas.addEventListener('pointerup', endDrag);
        canvas.addEventListener('pointercancel', endDrag);
        canvas.addEventListener('pointerleave', endDrag);
    }

    _startMobileDragLoop() {
        this._stopMobileDragLoop();
        const loop = () => {
            if (!this._dragActive) {
                this._mobileDragRafId = null;
                return;
            }
            const LERP = 0.5; // snappier than the hand-tracking ship — this is user-driven
            if (this._shipTargetX !== null) {
                this._shipX = this._shipX === null ? this._shipTargetX : this._shipX + (this._shipTargetX - this._shipX) * LERP;
                this._shipY = this._shipY === null ? this._shipTargetY : this._shipY + (this._shipTargetY - this._shipY) * LERP;
            }
            if (this.isSpaceMissionMode) {
                if (!this.spacePlanets.some(p => p.state === 'visiting')) {
                    this.drawSpaceMission();
                }
                this.checkPlanetContact();
            } else {
                if (!this.touchBubbles.some(b => b.state === 'popping')) {
                    this.drawTouchBubbles();
                    this._drawDragIndicator();
                }
                this.checkBubbleContactAtDragPoint();
            }
            this._mobileDragRafId = requestAnimationFrame(loop);
        };
        this._mobileDragRafId = requestAnimationFrame(loop);
    }

    _stopMobileDragLoop() {
        if (this._mobileDragRafId) {
            cancelAnimationFrame(this._mobileDragRafId);
            this._mobileDragRafId = null;
        }
    }

    /**
     * Classic Touch bubble contact check driven by the tracked finger position
     * instead of hand landmarks — mirrors checkPlanetContact()'s approach.
     */
    checkBubbleContactAtDragPoint() {
        if (this.currentTargetBubble >= this.touchBubbles.length) return;
        const bubble = this.touchBubbles[this.currentTargetBubble];
        if (bubble.state !== 'visible') return;
        if (this._shipX === null || this._shipY === null) return;

        const canvas = this.elements.overlay;
        const bx = bubble.x * canvas.width;
        const by = bubble.y * canvas.height;
        const dist = Math.sqrt((this._shipX - bx) ** 2 + (this._shipY - by) ** 2);
        const inContact = dist < bubble.radius * 1.3;

        if (inContact) {
            this.contactFrameCount++;
            if (this.contactFrameCount >= this.CONTACT_FRAMES_REQUIRED) {
                this.contactFrameCount = 0;
                this.popBubble(this.currentTargetBubble);
            }
        } else {
            this.contactFrameCount = Math.max(0, this.contactFrameCount - 1);
        }
    }

    /**
     * Simple finger-position indicator for classic Touch mode (no ship sprite)
     */
    _drawDragIndicator() {
        if (this._shipX === null || this._shipY === null) return;
        const ctx = this.elements.overlayCtx;
        ctx.save();
        ctx.beginPath();
        ctx.arc(this._shipX, this._shipY, 14, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(10, 37, 64, 0.85)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.restore();
    }

    /**
     * Enforce the "continuous motion" rule — lifting the finger before all
     * bubbles/planets are complete resets progress. The backend's step
     * counter is reset too so bubble_pop sequencing stays in sync.
     */
    resetMobileDragProgress() {
        if (this.isSpaceMissionMode) {
            this.spacePlanets.forEach(p => { p.state = 'visible'; p.popProgress = 0; p.rippleProgress = 0; });
            this.currentTargetPlanet = 0;
            this.drawSpaceMission();
        } else {
            this.touchBubbles.forEach(b => { b.state = 'visible'; b.popProgress = 0; b.rippleProgress = 0; });
            this.currentTargetBubble = 0;
            this.drawTouchBubbles();
        }
        this.contactFrameCount = 0;
        this.updateChallengeDisplay();

        if (this.elements.challengeText) {
            this.elements.challengeText.textContent = 'Finger lifted — starting over';
        }

        this._mobileDragEvents = [];

        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(JSON.stringify({ type: 'reset_progress' }));
        }
    }

    // ─────────────────────────────────────────────────────────────────────────

    clearCanvas() {
        const ctx = this.elements.overlayCtx;
        const canvas = this.elements.overlay;
        if (this.isSpaceMissionMode) {
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        } else {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    /**
     * Start collecting pointer events (from render until Start is clicked)
     */
    startPointerTracking() {
        this._pointerEvents = [];
        this._pointerHandler = (e) => {
            this._pointerEvents.push({
                x: e.clientX, y: e.clientY,
                pressure: e.pressure, width: e.width, height: e.height,
                timestamp: e.timeStamp, pointerType: e.pointerType
            });
        };
        document.addEventListener('pointermove', this._pointerHandler, { passive: true });
    }

    /**
     * Stop collecting pointer events
     */
    stopPointerTracking() {
        if (this._pointerHandler) {
            document.removeEventListener('pointermove', this._pointerHandler);
            this._pointerHandler = null;
        }
    }

    /**
     * Detect headless/automated browser environments
     * Returns true if 2+ signals indicate a non-human client
     */
    detectHeadlessBrowser() {
        let flags = 0;
        try { if (navigator.webdriver === true) flags++; } catch(e) {}
        try { if (navigator.plugins.length === 0 && /Chrome/.test(navigator.userAgent)) flags++; } catch(e) {}
        try { if (navigator.languages && navigator.languages.length === 0) flags++; } catch(e) {}
        try { if (!window.chrome && /Chrome/.test(navigator.userAgent) && !/Edg/.test(navigator.userAgent)) flags++; } catch(e) {}
        try { if (window.outerWidth === 0 && window.outerHeight === 0) flags++; } catch(e) {}
        return flags >= 2;
    }

    /**
     * Detect mobile/tablet devices using multiple signals (Client Hints when
     * available, falling back to UA + coarse-pointer + touch-support combo).
     * Requires the PRIMARY input to be touch, so touchscreen laptops (mouse as
     * primary input) are correctly excluded.
     */
    detectMobileDevice() {
        try {
            if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
                return navigator.userAgentData.mobile;
            }
        } catch (e) { /* ignore */ }

        const ua = navigator.userAgent || '';
        const uaMobile = /Android|iPhone|iPod|Windows Phone|Mobile/i.test(ua);
        // iPadOS 13+ reports a desktop Safari UA but exposes multi-touch
        const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        const uaTablet = /iPad/i.test(ua) || isIPadOS;

        let coarsePointer = false;
        try { coarsePointer = window.matchMedia('(pointer: coarse)').matches; } catch (e) { /* ignore */ }

        const hasTouch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;

        return (uaMobile || uaTablet) && coarsePointer && hasTouch;
    }

    /**
     * Score pointer movement 0.0–1.0 (low = bot-like, high = human-like)
     * Mouse: velocity variance + path curviness + event count
     * Touch: pressure variance + contact area
     */
    computePointerScore(events = this._pointerEvents, forceDragScoring = false) {
        if (!events || events.length === 0) return 0.0;

        const type = events[0].pointerType;

        // For touch events in a standard (non-drag) pre-capture context, use
        // pressure/area heuristics to detect simulated touch from automation.
        // But for Mobile Mode drag events (forceDragScoring), use velocity /
        // path curvature — the drag gesture has meaningful movement data and
        // iOS always reports pressure: 0, so pressure scoring would wrongly
        // penalise every real iPhone user.
        if (type === 'touch' && !forceDragScoring) {
            const pressures = events.map(e => e.pressure);
            const areas = events.map(e => e.width * e.height);
            const allZeroPressure = pressures.every(p => p === 0);
            const allHalfPressure = pressures.every(p => Math.abs(p - 0.5) < 0.001);
            const allUnitArea = areas.every(a => a <= 1.01);
            if ((allZeroPressure || allHalfPressure) && allUnitArea) return 0.1;
            if (allZeroPressure || allHalfPressure) return 0.3;
            return 0.75;
        }

        // Mouse / pen
        if (events.length < 3) return 0.2;

        const velocities = [];
        for (let i = 1; i < events.length; i++) {
            const dt = events[i].timestamp - events[i - 1].timestamp;
            const dx = events[i].x - events[i - 1].x;
            const dy = events[i].y - events[i - 1].y;
            if (dt > 0) velocities.push(Math.sqrt(dx * dx + dy * dy) / dt);
        }
        if (velocities.length === 0) return 0.2;

        const mean = velocities.reduce((a, b) => a + b, 0) / velocities.length;
        const variance = velocities.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / velocities.length;
        const cv = mean > 0 ? Math.sqrt(variance) / mean : 0;

        const first = events[0], last = events[events.length - 1];
        const totalDist = Math.sqrt(Math.pow(last.x - first.x, 2) + Math.pow(last.y - first.y, 2));
        let arcLength = 0;
        for (let i = 1; i < events.length; i++) {
            arcLength += Math.sqrt(Math.pow(events[i].x - events[i - 1].x, 2) + Math.pow(events[i].y - events[i - 1].y, 2));
        }
        const straightness = arcLength > 0 ? Math.min(1, totalDist / arcLength) : 1;

        const countScore = Math.min(1.0, events.length / 15);
        const varianceScore = Math.min(1.0, cv / 0.25);
        const curveScore = 1.0 - straightness;
        return Math.max(0, Math.min(1, 0.4 * countScore + 0.35 * varianceScore + 0.25 * curveScore));
    }

    /**
     * Reset capture - EXACT logic from AuthScreen
     */
    resetCapture() {
        this.stopFrameCapture();
        
        if (this.websocket) {
            this.websocket.close();
            this.websocket = null;
        }
        
        if (this.webcamStream) {
            this.webcamStream.getTracks().forEach(track => track.stop());
            this.webcamStream = null;
        }
        
        this.isCapturing = false;
        this.gesturesRecorded = 0;
        this.noHandsFrameCount = 0;
        this.hasStartedGestures = false;
        this.isTouchMode = false;
        this.touchBubbles = [];
        this.currentTargetBubble = 0;
        this.contactFrameCount = 0;
        this.isSpaceMissionMode = false;
        this.spacePlanets = [];
        this.currentTargetPlanet = 0;
        this._stopSpaceLoop();
        this._stopMobileDragLoop();
        this._dragActive = false;
        this._dragPointerId = null;
        this._mobileDragEvents = [];
        this._shipX = null;
        this._shipY = null;
        this._shipTargetX = null;
        this._shipTargetY = null;
        if (this.elements.videoWrapper) {
            this.elements.videoWrapper.classList.remove('space-mission');
        }
        this.stopPointerTracking();
        this._pointerEvents = [];
        this._pointerScore = null;
        this._headlessSuspect = false;
        this.startPointerTracking();
        
        this.elements.gestureCount.textContent = '0';
        this.elements.authStatus.textContent = 'Ready to verify';
        this.elements.authStatus.classList.remove('dexasign-status-success', 'dexasign-status-error');
        this.elements.loading.style.display = 'none';
        this.clearCanvas();
        
        this.elements.startBtn.style.display = 'inline-block';
        this.elements.resetBtn.style.display = 'none';

        // Restore instructions view
        this.elements.instructionList.style.display = '';
        this.elements.videoWrapper.style.display = 'none';
        this.elements.challengePrompt.style.display = 'none';
        this.elements.statusMessage.style.display = 'none';
        this.elements.authFooter.style.display = 'none';
        this.elements.challengeText.textContent = 'Loading challenge...';
        [this.elements.dot0, this.elements.dot1, this.elements.dot2].forEach(dot => {
            if (dot) {
                dot.style.background = '#E5E7EB';
                dot.style.borderColor = '#D1D5DB';
                dot.style.boxShadow = '';
            }
        });
        // Also reset any extra dots (4, 5) if present
        for (let i = 3; i < 5; i++) {
            const dot = this.elements[`dot${i}`];
            if (dot) { dot.style.background = '#E5E7EB'; dot.style.borderColor = '#D1D5DB'; dot.style.boxShadow = ''; }
        }
    }

    /**
     * Destroy and clean up - EXACT logic from AuthScreen
     */
    destroy() {
        this.resetCapture();
        if (this.websocket) {
            this.websocket.close();
        }
    }
}
